import axios from 'axios';

export interface AddressData {
  street: string;
  neighborhood: string;
  city: string;
  state: string;
  complement?: string;
}

interface ViaCepResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
  erro?: boolean;
}

interface BrasilApiResponse {
  cep: string;
  state: string;
  city: string;
  neighborhood: string;
  street: string;
  service: string;
  location: {
    type: string;
    coordinates: {
      longitude: string;
      latitude: string;
    };
  };
}

interface OpenCepResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  ibge: string;
  ddd: string;
  siafi: string;
}

class CepService {
  private normalizeCep(cep: string): string {
    return cep.replace(/\D/g, '');
  }

  private async fetchViaCep(cep: string): Promise<AddressData | null> {
    try {
      const response = await axios.get<ViaCepResponse>(
        `https://viacep.com.br/ws/${cep}/json/`,
        { timeout: 5000 }
      );

      if (response.data.erro) {
        return null;
      }

      if (!response.data.localidade || !response.data.uf) {
        return null;
      }

      return {
        street: response.data.logradouro || '',
        neighborhood: response.data.bairro || '',
        city: response.data.localidade,
        state: response.data.uf,
        complement: response.data.complemento || undefined
      };
    } catch (error) {
      console.warn('ViaCEP API failed:', error);
      return null;
    }
  }

  private async fetchBrasilApi(cep: string): Promise<AddressData | null> {
    try {
      const response = await axios.get<BrasilApiResponse>(
        `https://brasilapi.com.br/api/cep/v1/${cep}`,
        { timeout: 5000 }
      );

      if (!response.data.city || !response.data.state) {
        return null;
      }

      return {
        street: response.data.street || '',
        neighborhood: response.data.neighborhood || '',
        city: response.data.city,
        state: response.data.state
      };
    } catch (error) {
      console.warn('BrasilAPI failed:', error);
      return null;
    }
  }

  private async fetchOpenCep(cep: string): Promise<AddressData | null> {
    try {
      const response = await axios.get<OpenCepResponse>(
        `https://opencep.com/v1/${cep}`,
        { timeout: 5000 }
      );

      if (!response.data.cidade || !response.data.estado) {
        return null;
      }

      return {
        street: response.data.logradouro || '',
        neighborhood: response.data.bairro || '',
        city: response.data.cidade,
        state: response.data.estado,
        complement: response.data.complemento || undefined
      };
    } catch (error) {
      console.warn('OpenCEP API failed:', error);
      return null;
    }
  }

  async searchCep(cep: string): Promise<AddressData | null> {
    const normalizedCep = this.normalizeCep(cep);
    if (normalizedCep.length !== 8) {
      return null;
    }

    const apis = [
      { name: 'ViaCEP', fn: () => this.fetchViaCep(normalizedCep) },
      { name: 'BrasilAPI', fn: () => this.fetchBrasilApi(normalizedCep) },
      { name: 'OpenCEP', fn: () => this.fetchOpenCep(normalizedCep) }
    ];

    for (const api of apis) {
      try {
        console.log(`Trying ${api.name} for CEP ${normalizedCep}...`);
        const address = await api.fn();
        if (address) {
          console.log(`${api.name} returned address data:`, address);
          return address;
        }
        console.log(`${api.name} returned null, trying next API...`);
      } catch (error) {
        console.warn(`${api.name} failed:`, error);
        continue;
      }
    }

    console.warn(`All APIs failed for CEP ${normalizedCep}`);
    return null;
  }

  formatCep(cep: string): string {
    const normalized = this.normalizeCep(cep);
    return normalized.replace(/(\d{5})(\d{3})/, '$1-$2');
  }

  isValidCep(cep: string): boolean {
    const normalized = this.normalizeCep(cep);
    return normalized.length === 8 && /^\d{8}$/.test(normalized);
  }
}

export const cepService = new CepService();
